/**
 * FollowUpScoreGauge.jsx — M2/M7 (Phase 2 Engagement)
 * ===================================================
 * Jauge "Score de suivi vendeur" par mandat. Monte EN DIRECT après chaque action
 * (recency/volume/complétude/statut). Échelle bienveillante (jamais "mauvais").
 * Inclut le nudge "Vous êtes proche de…" (effet Zeigarnik).
 */
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import api from '../../lib/api';
import { Gauge, ArrowRight, ShieldCheck } from 'lucide-react';

const TONE = {
    excellent: { color: '#34D399', from: '#10B981', to: '#34D399' },
    good: { color: '#A855F7', from: '#681793', to: '#A855F7' },
    watch: { color: '#FBBF24', from: '#F59E0B', to: '#FBBF24' },
    reactivate: { color: '#FF914D', from: '#E66A1A', to: '#FF914D' },
};

export default function FollowUpScoreGauge({ mandateId, refreshKey = 0 }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const r = await api.get(`/engagement/mandate/${mandateId}/follow-up-score`);
                if (alive) setData(r.data);
            } catch (e) {
                // silencieux
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [mandateId, refreshKey]);

    if (loading) {
        return (
            <div className="rounded-2xl border border-border bg-card p-4 mb-4 animate-pulse" data-testid="follow-up-score-loading">
                <div className="h-3 w-40 bg-white/5 rounded mb-3" />
                <div className="h-2.5 w-full bg-white/5 rounded" />
            </div>
        );
    }
    if (!data) return null;

    const tone = TONE[data.tone] || TONE.good;

    return (
        <div
            className="rounded-2xl border border-border bg-card p-4 mb-4"
            data-testid="follow-up-score"
        >
            <div className="flex items-center justify-between mb-2.5">
                <div className="flex items-center gap-2">
                    <Gauge className="w-4 h-4" style={{ color: tone.color }} />
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                        Score de suivi vendeur
                    </p>
                </div>
                <span
                    className="text-[11px] font-bold px-2 py-0.5 rounded-full"
                    style={{ background: `${tone.color}1f`, color: tone.color }}
                    data-testid="follow-up-label"
                >
                    {data.label}
                </span>
            </div>

            <div className="flex items-end gap-3">
                <motion.span
                    key={data.score}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-3xl font-black leading-none"
                    style={{ color: tone.color }}
                    data-testid="follow-up-value"
                >
                    {data.score}
                </motion.span>
                <span className="text-xs text-muted-foreground mb-1">/ 100</span>
            </div>

            {/* Barre de progression animée */}
            <div className="mt-2 h-2 rounded-full bg-white/5 overflow-hidden">
                <motion.div
                    className="h-full rounded-full"
                    style={{ background: `linear-gradient(90deg, ${tone.from}, ${tone.to})` }}
                    initial={{ width: 0 }}
                    animate={{ width: `${data.score}%` }}
                    transition={{ type: 'spring', stiffness: 120, damping: 20 }}
                />
            </div>

            {/* Nudge M7 — "vous êtes proche de…" */}
            <div className="mt-3 flex items-start gap-2">
                <ArrowRight className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: tone.color }} />
                <p className="text-xs text-foreground/80 leading-snug" data-testid="follow-up-next-step">
                    {data.next_step}
                    {data.points_to_next != null && data.next_label && (
                        <span className="text-muted-foreground">
                            {' '}— plus que <span className="font-semibold text-foreground">{data.points_to_next} pts</span> pour passer en « {data.next_label} ».
                        </span>
                    )}
                </p>
            </div>

            {data.tone === 'excellent' && (
                <div className="mt-2 flex items-center gap-1.5 text-[11px]" style={{ color: tone.color }}>
                    <ShieldCheck className="w-3.5 h-3.5" />
                    Ce vendeur est entre de bonnes mains.
                </div>
            )}
        </div>
    );
}
